import React, { useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { getDeviceContacts } from '../api/deviceContacts';
import type { DeviceContact } from '../types/connections';
import { TerminalBlock } from '../types';

// NEW: Interface for terminal_block_assignments data
interface TerminalBlockAssignment {
  id: number;
  terminal_block_id: number;
  device_instance_id: number;
  specification_id: number;  // Added: for loading contacts from specification
  position_in_block: number;
  properties?: any;
  created_at?: string;
}

interface TerminalBlockNodeData {
  terminalBlock: TerminalBlock;
  assignments: TerminalBlockAssignment[]; // Changed from instances to assignments
}

interface TerminalBlockNodeProps {
  data: TerminalBlockNodeData;
}

interface TerminalInstanceNodeProps {
  position: number;
  deviceInstanceId: number; // RESTORED: Needed for handle ID
  specificationId: number;  // Added: for loading contacts from specification
  specificationName?: string;
      // isConnecting?: boolean; - TEMPORARILY COMMENTED
  connectingSourceHandle?: string | null;
}

const TerminalInstanceNode: React.FC<TerminalInstanceNodeProps> = ({ 
  position,
  deviceInstanceId, 
  specificationId
}) => {
  const [contacts, setContacts] = useState<DeviceContact[]>([]);
  
  useEffect(() => {
    if (!specificationId || typeof specificationId !== 'number') {
      return;
    }
    
    const loadContacts = async () => {
      try {
        const specificationContacts = await getDeviceContacts(specificationId);
        setContacts(specificationContacts);
      } catch (error) {
        console.error(`❌ Error loading contacts for specification ${specificationId}:`, error);
        setContacts([]);
      }
    };
    
    loadContacts();
  }, [specificationId]);
  
  // Filter contacts by position (U = upper, D = lower) - use contact_label
  const upperContacts = contacts.filter(contact => 
    typeof contact.contact_label === 'string' && contact.contact_label.endsWith('U')
  ).sort((a, b) => {
    const aNum = parseInt(String(a.contact_label).replace('U', ''));
    const bNum = parseInt(String(b.contact_label).replace('U', ''));
    return aNum - bNum;
  });
  
  const lowerContacts = contacts.filter(contact => 
    typeof contact.contact_label === 'string' && contact.contact_label.endsWith('D')
  ).sort((a, b) => {
    const aNum = parseInt(String(a.contact_label).replace('D', ''));
    const bNum = parseInt(String(b.contact_label).replace('D', ''));
    return aNum - bNum;
  });

  // DEBUG: Log contacts and Handle IDs (disabled for production)
  // console.log(`🔍 TerminalInstanceNode position=${position}: deviceInstanceId=${deviceInstanceId}, specificationId=${specificationId}`);
  // console.log(`🔍 Contacts loaded:`, contacts.length, contacts);
  // console.log(`🔍 Lower contacts:`, lowerContacts.length, lowerContacts.map(c => ({ id: c.id, label: c.contact_label })));
  // if (lowerContacts.length > 0) {
  //   console.log(`🔍 Sample Handle ID would be: ${deviceInstanceId}-${lowerContacts[0].id}`);
  // }
  
  return (
    <div className="terminal-instance-block bg-white border border-gray-300 rounded p-1.5 w-8 shadow-sm">
      {/* Terminal header */}
      <div className="terminal-instance-header text-center mb-1.5 border-b border-gray-200 pb-1">
        <div className="terminal-instance-name font-bold text-xs text-gray-800">{position}</div>
        <div className="terminal-instance-type text-xs text-gray-600 mt-0.5">
          {/* specificationName || `T${position}` */}
          Terminal
        </div>
      </div>
      
      {/* Contacts container */}
      <div className="terminal-contacts-container flex justify-center">
        <div className="terminal-contacts-grid grid gap-0.5 p-1" style={{ gridTemplateRows: 'repeat(3, 1fr) auto repeat(3, 1fr)', gap: '2px' }}>
          
          {/* Upper contacts - 1U, 2U, 3U */}
          {upperContacts.map((contact) => (
            <div key={contact.id} className="terminal-contact-circle w-5 h-5 rounded-full bg-gray-300 border-2 border-gray-600 cursor-pointer flex items-center justify-center text-xs font-bold text-gray-800 transition-all duration-200 hover:bg-blue-500 hover:border-blue-700 hover:text-white hover:scale-110">
              {contact.contact_number}
          </div>
          ))}
          
          {/* Spacer between upper and lower contacts */}
          <div className="h-3"></div>
          
          {/* Lower contacts with connection handles - 3D, 2D, 1D */}
          {lowerContacts.reverse().map((contact) => (
            <div key={contact.id} className="relative terminal-contact-circle w-5 h-5 rounded-full bg-gray-300 border-2 border-gray-600 cursor-pointer flex items-center justify-center text-xs font-bold text-gray-800 transition-all duration-200 hover:bg-green-500 hover:border-green-700 hover:text-white hover:scale-110">
              {contact.contact_number}
              {/* Source handle (outgoing connections) - только source для терминальных клеммников */}
            <Handle
              type="source"
              position={Position.Left}
                id={`${deviceInstanceId}-${contact.id}-source`} // FIXED: Include device_instance_id for unique handles
              className="schema-source-handle w-3 h-3 bg-green-500 border border-green-700"
                style={{ 
                  position: 'absolute', 
                  left: '0px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  borderRadius: '50%',
                  zIndex: 1000,
                  pointerEvents: 'all'
                }}
              isConnectable={true}
            />
          </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const TerminalBlockNode: React.FC<TerminalBlockNodeProps> = ({ data }) => {
  const { terminalBlock, assignments } = data;
  
  if (!assignments || assignments.length === 0) {
    return (
      <div className="terminal-block-container bg-white border-2 border-red-500 rounded-lg p-4 shadow-lg">
        <div className="text-center text-red-600">
          <div className="font-bold">⚠️ No Terminal Assignments</div>
          <div className="text-sm mt-1">Block ID: {terminalBlock.id}</div>
        </div>
      </div>
    );
  }

  // Sort assignments by position_in_block
  const sortedAssignments = [...assignments].sort((a, b) => a.position_in_block - b.position_in_block);
  
  return (
    <div className="terminal-block-container bg-white border-2 border-blue-500 rounded-lg p-4 shadow-lg" style={{ width: 'fit-content', minWidth: '300px' }}>
      {/* Terminal Block Header */}
      <div className="terminal-block-header text-center mb-4 border-b border-blue-200 pb-2">
        <div className="terminal-block-name font-bold text-lg text-blue-800">
          {terminalBlock.name || `Terminal Block ${terminalBlock.id}`}
        </div>
        <div className="terminal-block-info text-sm text-blue-600 mt-1">
          {assignments.length} Terminals • Block ID: {terminalBlock.id}
        </div>
      </div>
      
      {/* Terminal Instances Grid */}
      <div className="terminal-instances-container">
        <div 
          className="terminal-instances-grid flex flex-row gap-2 items-center"
          style={{ 
            flexWrap: 'nowrap',
            overflowX: 'visible'
          }}
        >
          {sortedAssignments.map((assignment) => (
            <TerminalInstanceNode
              key={assignment.id}
              position={assignment.position_in_block}
              deviceInstanceId={assignment.device_instance_id}
              specificationId={assignment.specification_id} // Pass specificationId
              // specificationName="Terminal" // Could load from device_instances if needed
              // isConnecting={isConnecting} - TEMPORARILY COMMENTED
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default TerminalBlockNode; 